/* Las operaciones de FiboSheets sobre una hoja: copiar, pegar, rellenar,
 * insertar, borrar, ordenar, y cómo se muestra un valor.
 *
 * ── POR QUÉ APARTE DE LA PANTALLA ──────────────────────────────────────────
 *
 * Son las que pueden romper una planilla ajena: correr mal una fórmula al
 * insertar una fila, o pegar un bloque una columna más allá. Acá son funciones
 * puras —entra una hoja, sale otra— y se prueban sin dibujar nada. La pantalla
 * sólo decide CUÁNDO llamarlas.
 *
 * Una hoja es `{ nombre, celdas, formato, anchos, altos, fija }`: `celdas` es
 * la matriz de textos crudos, `formato` va por referencia ("B3"), `anchos` por
 * índice de columna y `altos` por índice de fila (y, si las tiene, sus reglas
 * `condicional` y `validacion`: ver `shared/formatoCondicional`; y sus celdas
 * `combinadas`: ver `shared/planillaCombinadas`). Si tiene tablas del hub
 * volcadas, `tablasHub` dice dónde está cada una (ver `shared/tablasHub`).
 * Ninguna función modifica lo
 * que recibe, y las filas que no cambian se devuelven tal cual (la grilla se
 * apoya en eso para no redibujar de más).
 */

import {
  ajustarFormula,
  CARGANDO,
  desplazarFormula,
  fechaASerial,
  numeroLiteral,
  parseRef,
  refDe,
  serialAFecha,
} from "./planilla";
import { ajustarReglasHoja, pegarReglas, reglasDelRango } from "./formatoCondicional";
import { ajustarCombinadas, ordenarCombinadas } from "./planillaCombinadas";

/* ── Mostrar un valor ───────────────────────────────────────────────────────
 *
 * Sin formato, un número se muestra como lo que es: con coma decimal y SIN
 * puntos de miles. Un DNI, un teléfono, un interno o un año son números y con
 * puntos se leen mal (2.026, 1.128.524.957). Los miles se piden con el formato. */

const formateadores = new Map();
function formateador(min, max, miles) {
  const k = `${min}|${max}|${miles ? 1 : 0}`;
  let f = formateadores.get(k);
  if (!f) {
    f = new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: min, maximumFractionDigits: max, useGrouping: miles,
    });
    formateadores.set(k, f);
  }
  return f;
}

/* Una fracción de día (0,5 = mediodía) → "12:00:00". */
function horaDe(fraccion) {
  const seg = Math.round((((fraccion % 1) + 1) % 1) * 86400) % 86400;
  return [Math.floor(seg / 3600), Math.floor((seg % 3600) / 60), seg % 60]
    .map((x) => String(x).padStart(2, "0")).join(":");
}

/* Los formatos de número, como los ofrece el menú «123» (`num` en el formato
   de la celda). Una fecha o una hora es un número —días desde 1899, como en
   Excel—, así que también se le puede dar formato de fecha a un número. */
export const FORMATOS_NUMERO = [
  ["", "Automático", "1000,12"],
  ["numero", "Número", "1.000,12"],
  ["pct", "Porcentaje", "10,12 %"],
  ["cientifica", "Científico", "1,01E+03"],
  ["moneda", "Moneda", "$ 1.000,12"],
  ["moneda0", "Moneda redondeada", "$ 1.000"],
  ["miles", "Con puntos de miles", "1.000"],
  ["fecha", "Fecha", "26/09/2008"],
  ["hora", "Hora", "15:59:00"],
  ["fechahora", "Fecha y hora", "26/09/2008 15:59:00"],
  ["duracion", "Duración", "24:01:00"],
];

export function formatear(v, fmt) {
  if (v === "" || v == null) return "";
  if (v === CARGANDO) return "…"; // un dato del hub (HUB.…) que está viajando
  if (typeof v === "boolean") return v ? "VERDADERO" : "FALSO";
  if (typeof v !== "number") return String(v);
  const f = fmt || {};
  const dec = Number.isInteger(f.dec) ? Math.min(10, Math.max(0, f.dec)) : null;
  if (f.num === "fecha") return serialAFecha(Math.floor(v));
  if (f.num === "hora") return horaDe(v);
  if (f.num === "fechahora") return `${serialAFecha(Math.floor(v))} ${horaDe(v)}`;
  if (f.num === "duracion") {
    const seg = Math.round(Math.abs(v) * 86400);
    return `${v < 0 ? "-" : ""}${Math.floor(seg / 3600)}:${horaDe(seg / 86400).slice(3)}`;
  }
  if (f.num === "cientifica") {
    const [m, e] = v.toExponential(dec ?? 2).split("e");
    return `${m.replace(".", ",")}E${e[0] === "-" ? "-" : "+"}${e.replace(/^[+-]/, "").padStart(2, "0")}`;
  }
  if (f.num === "numero") return formateador(dec ?? 2, dec ?? 2, true).format(v);
  if (f.num === "moneda0") return "$ " + formateador(dec ?? 0, dec ?? 0, true).format(v);
  if (f.num === "moneda") return "$ " + formateador(dec ?? 2, dec ?? 2, true).format(v);
  if (f.num === "pct") return formateador(dec ?? 0, dec ?? 2, false).format(v * 100) + " %";
  if (f.num === "miles") return formateador(dec ?? 0, dec ?? 0, true).format(v);
  if (dec != null) return formateador(dec, dec, false).format(v);
  return formateador(0, 10, false).format(v);
}

/* ── Rangos ─────────────────────────────────────────────────────────────── */

export function rangoDe(a, b) {
  const o = b || a;
  return {
    r0: Math.min(a.r, o.r), r1: Math.max(a.r, o.r),
    c0: Math.min(a.c, o.c), c1: Math.max(a.c, o.c),
  };
}

const anchoDe = (celdas) => (celdas[0] ? celdas[0].length : 0);

/* ── Portapapeles: el texto que entiende Excel ──────────────────────────────
 * Filas separadas por salto de línea, celdas por tabulador. Una celda con
 * tabulador, salto o comillas va entre comillas, con las comillas dobladas. */

export function aTSV(matriz) {
  return matriz.map((fila) => fila.map((x) => {
    const s = x == null ? "" : String(x);
    return /[\t\n\r"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join("\t")).join("\n");
}

export function deTSV(texto) {
  const s = String(texto ?? "").replace(/\r\n?/g, "\n");
  const filas = [];
  let fila = [];
  let celda = "";
  let i = 0;
  const cerrarCelda = () => { fila.push(celda); celda = ""; };
  const cerrarFila = () => { cerrarCelda(); filas.push(fila); fila = []; };
  while (i < s.length) {
    const ch = s[i];
    if (ch === '"' && celda === "") { // una celda entre comillas
      let j = i + 1;
      let dentro = "";
      let cerrada = false;
      while (j < s.length) {
        if (s[j] === '"') {
          if (s[j + 1] === '"') { dentro += '"'; j += 2; continue; }
          cerrada = true; j += 1; break;
        }
        dentro += s[j]; j += 1;
      }
      // Sólo vale como celda entre comillas si después viene un separador; si
      // no, era un texto que empezaba con comillas y se toma literal.
      if (cerrada && (j >= s.length || s[j] === "\t" || s[j] === "\n")) { celda = dentro; i = j; continue; }
    }
    if (ch === "\t") { cerrarCelda(); i += 1; continue; }
    if (ch === "\n") { cerrarFila(); i += 1; continue; }
    celda += ch; i += 1;
  }
  // Excel termina con un salto de línea: eso no es una fila vacía más.
  if (celda !== "" || fila.length) cerrarFila();
  const cols = Math.max(1, ...filas.map((f) => f.length));
  return filas.length ? filas.map((f) => { const g = f.slice(); while (g.length < cols) g.push(""); return g; }) : [[""]];
}

/* ── Leer y escribir bloques ────────────────────────────────────────────── */

export function copiarRango(hoja, rg) {
  const crudos = [];
  const formatos = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    const fc = [];
    const ff = [];
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      fc.push(hoja.celdas[r] && hoja.celdas[r][c] != null ? hoja.celdas[r][c] : "");
      ff.push((hoja.formato && hoja.formato[refDe(r, c)]) || null);
    }
    crudos.push(fc); formatos.push(ff);
  }
  // Las reglas de formato condicional y las listas desplegables que tocan lo
  // copiado viajan aparte: son de un rango, no de cada celda.
  return { crudos, formatos, reglas: reglasDelRango(hoja, rg) };
}

/* Escribe `cambios` —una lista de `{ r, c, v, fmt }`— de una sola pasada. La
   hoja crece si hace falta, y queda siempre una fila y una columna libres más
   allá de lo último escrito, igual que al tipear. `fmt` undefined no toca el
   formato de esa celda; null se lo saca. */
export function escribir(hoja, cambios) {
  if (!cambios.length) return hoja;
  const filas0 = hoja.celdas.length;
  const cols0 = anchoDe(hoja.celdas);
  let filas = filas0;
  let cols = cols0;
  for (const k of cambios) {
    const conDato = k.v !== undefined && k.v !== "";
    filas = Math.max(filas, k.r + (conDato ? 2 : 1));
    cols = Math.max(cols, k.c + (conDato ? 2 : 1));
  }
  const celdas = hoja.celdas.slice();
  const copiada = new Set();
  const filaEditable = (r) => {
    if (!copiada.has(r)) {
      celdas[r] = celdas[r] ? celdas[r].slice() : [];
      while (celdas[r].length < cols) celdas[r].push("");
      copiada.add(r);
    }
    return celdas[r];
  };
  if (cols > cols0) for (let r = 0; r < filas0; r += 1) filaEditable(r);
  for (let r = filas0; r < filas; r += 1) filaEditable(r);
  let formato = hoja.formato || {};
  let tocoFormato = false;
  for (const k of cambios) {
    if (k.v !== undefined) filaEditable(k.r)[k.c] = k.v;
    if (k.fmt !== undefined) {
      if (!tocoFormato) { formato = { ...formato }; tocoFormato = true; }
      const ref = refDe(k.r, k.c);
      if (k.fmt && Object.keys(k.fmt).length) formato[ref] = k.fmt; else delete formato[ref];
    }
  }
  return { ...hoja, celdas, formato };
}

export function borrarRango(hoja, rg) {
  const cambios = [];
  for (let r = rg.r0; r <= Math.min(rg.r1, hoja.celdas.length - 1); r += 1) {
    for (let c = rg.c0; c <= Math.min(rg.c1, anchoDe(hoja.celdas) - 1); c += 1) {
      if (hoja.celdas[r][c] !== "") cambios.push({ r, c, v: "" });
    }
  }
  return escribir(hoja, cambios);
}

/* Pega un bloque con su esquina en `destino`. Si viene de esta misma planilla
 * (`origen`), las fórmulas se corren lo que se corrió el bloque —salvo que se
 * haya CORTADO: mover no cambia a qué apunta una fórmula— y viajan los
 * formatos. Si `hasta` es un rango más grande que el bloque, el bloque se
 * repite hasta llenarlo (copiar una celda y pegarla en veinte). */
export function pegar(hoja, destino, bloque, opciones = {}) {
  const { origen = null, formatos = null, cortar = false, hasta = null, reglas = null } = opciones;
  const alto = bloque.length;
  const ancho = bloque[0] ? bloque[0].length : 0;
  if (!alto || !ancho) return hoja;
  const repetir = (pedido, propio) => (pedido > propio && pedido % propio === 0 ? pedido / propio : 1);
  const vecesF = hasta ? repetir(hasta.r1 - hasta.r0 + 1, alto) : 1;
  const vecesC = hasta ? repetir(hasta.c1 - hasta.c0 + 1, ancho) : 1;
  const cambios = [];
  for (let i = 0; i < alto * vecesF; i += 1) {
    for (let j = 0; j < ancho * vecesC; j += 1) {
      const r = destino.r + i;
      const c = destino.c + j;
      let v = bloque[i % alto][j % ancho];
      if (origen && !cortar) v = desplazarFormula(v, r - (origen.r0 + (i % alto)), c - (origen.c0 + (j % ancho)));
      const cambio = { r, c, v: v == null ? "" : String(v) };
      if (formatos) cambio.fmt = formatos[i % alto][j % ancho];
      cambios.push(cambio);
    }
  }
  const escrita = escribir(hoja, cambios);
  // Con el bloque viajan sus reglas (ver `pegarReglas`), una vez: no se
  // repiten al llenar un rango más grande.
  return reglas && origen ? pegarReglas(escrita, reglas, origen, destino) : escrita;
}

/* ── Rellenar (arrastrar el cuadradito) ─────────────────────────────────────
 *
 * `origen` es lo seleccionado y `destino` hasta dónde se arrastró (lo incluye).
 * Columna por columna —o fila por fila, si se arrastró de costado— se mira qué
 * hay en el origen:
 *   · fórmulas: se copian corriendo sus referencias;
 *   · dos o más números, fechas o «Texto 1, Texto 2» con paso parejo: sigue la
 *     serie;
 *   · una fecha sola, un «Texto 1» solo, un día o un mes: sigue de a uno;
 *   · cualquier otra cosa (un número solo incluido): se repite.
 * Con `serie: false` (Ctrl+D) siempre se repite. */

const DIAS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const comoLaOriginal = (palabra, original) => {
  if (original === original.toUpperCase()) return palabra.toUpperCase();
  if (original[0] === original[0].toUpperCase()) return palabra[0].toUpperCase() + palabra.slice(1);
  return palabra;
};

/* El paso parejo de una lista de números, o null si no lo tiene. */
function pasoDe(xs) {
  if (xs.length < 2) return null;
  const paso = xs[1] - xs[0];
  for (let i = 2; i < xs.length; i += 1) if (Math.abs((xs[i] - xs[i - 1]) - paso) > 1e-9) return null;
  return paso;
}

/* Devuelve la función «el texto que va en la posición n» (n = 0 es la primera
   celda del origen; puede ser negativa si se arrastra para atrás), o null si
   ese origen no arma una serie. */
function serieDe(fuentes) {
  const n = fuentes.length;
  if (!n || fuentes.some((x) => x === "" || String(x).startsWith("="))) return null;

  const nums = fuentes.map(numeroLiteral);
  if (nums.every((x) => x !== null)) {
    const paso = pasoDe(nums);
    if (paso === null) return null; // un número solo se repite, como en Excel
    const conComa = fuentes.some((x) => String(x).includes(","));
    return (i) => {
      const s = String(Math.round((nums[0] + paso * i) * 1e10) / 1e10);
      return conComa ? s.replace(".", ",") : s;
    };
  }

  const fechas = fuentes.map(fechaASerial);
  if (fechas.every((x) => x !== null)) {
    const paso = n === 1 ? 1 : pasoDe(fechas);
    return paso === null ? null : (i) => serialAFecha(fechas[0] + paso * i);
  }

  for (const lista of [DIAS, MESES]) {
    const pos = fuentes.map((x) => lista.indexOf(String(x).trim().toLowerCase()));
    if (pos.every((p) => p >= 0)) {
      const paso = n === 1 ? 1 : pasoDe(pos);
      if (paso === null) return null;
      const largo = lista.length;
      return (i) => comoLaOriginal(lista[(((pos[0] + paso * i) % largo) + largo) % largo], String(fuentes[0]).trim());
    }
  }

  const partes = fuentes.map((x) => /^(.*?)(\d+)$/.exec(String(x)));
  if (partes.every((m) => m && m[1] === partes[0][1])) {
    const paso = n === 1 ? 1 : pasoDe(partes.map((m) => parseInt(m[2], 10)));
    if (paso === null) return null;
    const base = parseInt(partes[0][2], 10);
    const largo = partes[0][2].length; // «Camión 01» sigue «Camión 02»
    return (i) => {
      const v = base + paso * i;
      return v < 0 ? null : partes[0][1] + String(v).padStart(largo, "0");
    };
  }
  return null;
}

export function rellenar(hoja, origen, destino, opciones = {}) {
  const usarSerie = opciones.serie !== false;
  const vertical = destino.r0 < origen.r0 || destino.r1 > origen.r1;
  const horizontal = !vertical && (destino.c0 < origen.c0 || destino.c1 > origen.c1);
  if (!vertical && !horizontal) return hoja;
  const cambios = [];
  // `linea` recorre lo que no se arrastra (las columnas, si se arrastró hacia
  // abajo) y `p` lo que sí.
  const [l0, l1] = vertical ? [origen.c0, origen.c1] : [origen.r0, origen.r1];
  const [o0, o1] = vertical ? [origen.r0, origen.r1] : [origen.c0, origen.c1];
  const [d0, d1] = vertical ? [destino.r0, destino.r1] : [destino.c0, destino.c1];
  const largo = o1 - o0 + 1;
  const celda = (linea, p) => (vertical ? { r: p, c: linea } : { r: linea, c: p });
  const cruda = ({ r, c }) => (hoja.celdas[r] && hoja.celdas[r][c] != null ? hoja.celdas[r][c] : "");
  for (let linea = l0; linea <= l1; linea += 1) {
    const fuentes = [];
    for (let p = o0; p <= o1; p += 1) fuentes.push(cruda(celda(linea, p)));
    const serie = usarSerie ? serieDe(fuentes) : null;
    for (let p = d0; p <= d1; p += 1) {
      if (p >= o0 && p <= o1) continue;
      const i = p - o0; // posición respecto del principio del origen
      const k = ((i % largo) + largo) % largo; // de cuál celda del origen se copia
      const de = celda(linea, o0 + k);
      const a = celda(linea, p);
      const deSerie = serie ? serie(i) : null;
      const v = deSerie != null ? deSerie : desplazarFormula(fuentes[k], a.r - de.r, a.c - de.c);
      cambios.push({ ...a, v, fmt: (hoja.formato && hoja.formato[refDe(de.r, de.c)]) || null });
    }
  }
  return escribir(hoja, cambios);
}

/* ── Insertar y borrar filas o columnas ─────────────────────────────────────
 *
 * Trabajan sobre el LIBRO, no sobre la hoja: una fórmula de otra hoja que mira
 * a ésta también hay que corregirla. `cuantas` > 0 inserta antes de `desde`;
 * < 0 borra desde `desde`. */

function correrIndices(mapa, desde, cuantas) {
  const out = {};
  const borradas = cuantas < 0 ? -cuantas : 0;
  for (const [k, v] of Object.entries(mapa || {})) {
    const i = Number(k);
    if (i < desde) out[i] = v;
    else if (cuantas > 0) out[i + cuantas] = v;
    else if (i >= desde + borradas) out[i - borradas] = v;
  }
  return out;
}

function correrFormato(formato, eje, desde, cuantas) {
  const out = {};
  const borradas = cuantas < 0 ? -cuantas : 0;
  for (const [ref, f] of Object.entries(formato || {})) {
    const p = parseRef(ref);
    if (!p) continue;
    let i = eje === "fila" ? p.fila : p.col;
    if (i >= desde) {
      if (cuantas > 0) i += cuantas;
      else if (i >= desde + borradas) i -= borradas;
      else continue; // la celda se borró: su formato también
    }
    out[eje === "fila" ? refDe(i, p.col) : refDe(p.fila, i)] = f;
  }
  return out;
}

/* Las tablas del hub volcadas en la hoja (`hoja.tablasHub`, ver
   `shared/tablasHub`) recuerdan dónde quedó su bloque para poder
   «Actualizar». Si el bloque se corre con sus celdas y la definición no,
   «Actualizar» escribiría en el lugar viejo, encima de lo que quedó ahí.
     · Antes del bloque: se corre entero.
     · Adentro: el bloque crece o se achica, como una tabla de Excel; si se
       INSERTÓ adentro queda `retocada`, porque lo que se escriba en ese hueco
       se va a pisar al actualizar y hay que avisarlo antes.
     · Si se borró entero, la tabla se olvida (los valores ya no están). */
export function correrTablasHub(tablas, eje, desde, cuantas) {
  if (!Array.isArray(tablas) || !tablas.length || !cuantas) return tablas;
  let cambio = false;
  const out = [];
  for (const t of tablas) {
    const p = parseRef(t && t.desde);
    if (!p) { out.push(t); continue; }
    const ini = eje === "fila" ? p.fila : p.col;
    const largo = eje === "fila" ? t.filas : t.cols;
    let nIni = ini;
    let nLargo = largo;
    let retocada = false;
    if (cuantas > 0) {
      if (desde <= ini) nIni = ini + cuantas;
      else if (desde < ini + largo) { nLargo = largo + cuantas; retocada = true; }
    } else {
      const fin = desde - cuantas; // exclusivo
      const antes = Math.max(0, Math.min(fin, ini) - desde);
      const dentro = Math.max(0, Math.min(fin, ini + largo) - Math.max(desde, ini));
      nIni = ini - antes;
      nLargo = largo - dentro;
    }
    if (nLargo <= 0) { cambio = true; continue; }
    if (nIni === ini && nLargo === largo) { out.push(t); continue; }
    cambio = true;
    const n = eje === "fila"
      ? { ...t, desde: refDe(nIni, p.col), filas: nLargo }
      : { ...t, desde: refDe(p.fila, nIni), cols: nLargo };
    if (retocada) n.retocada = true;
    out.push(n);
  }
  return cambio ? out : tablas;
}

export function cambiarEstructura(hojas, indice, eje, desde, cuantas) {
  const objetivo = hojas[indice];
  if (!objetivo || !cuantas) return hojas;
  const filas = objetivo.celdas.length;
  const cols = anchoDe(objetivo.celdas);
  const total = eje === "fila" ? filas : cols;
  // No se puede borrar todo: siempre queda una fila y una columna.
  const n = cuantas < 0 ? -Math.min(-cuantas, total - 1, total - desde) : cuantas;
  if (!n) return hojas;
  const cambio = { eje, desde, cuantas: n, hoja: objetivo.nombre };

  return hojas.map((h, i) => {
    // 1) Las fórmulas de TODAS las hojas que apuntan a la que cambió.
    let tocada = false;
    let celdas = h.celdas.map((fila) => {
      let nueva = null;
      for (let c = 0; c < fila.length; c += 1) {
        const v = fila[c];
        if (typeof v === "string" && v.startsWith("=")) {
          const a = ajustarFormula(v, { ...cambio, hojaFormula: h.nombre });
          if (a !== v) { if (!nueva) nueva = fila.slice(); nueva[c] = a; }
        }
      }
      if (nueva) tocada = true;
      return nueva || fila;
    });
    // El formato condicional y las listas desplegables: sus rangos (en la hoja
    // que cambió) y sus fórmulas (en todas). Ver `shared/formatoCondicional`.
    const reglas = ajustarReglasHoja(h, cambio, i === indice);
    if (i !== indice) return tocada || reglas ? { ...h, celdas, ...reglas } : h;

    // 2) Y en la hoja que cambió, mover las celdas, el formato y los tamaños.
    if (eje === "fila") {
      if (n > 0) {
        const vacias = Array.from({ length: n }, () => Array(cols).fill(""));
        celdas = [...celdas.slice(0, desde), ...vacias, ...celdas.slice(desde)];
      } else {
        celdas = [...celdas.slice(0, desde), ...celdas.slice(desde - n)];
      }
    } else if (n > 0) {
      celdas = celdas.map((f) => [...f.slice(0, desde), ...Array(n).fill(""), ...f.slice(desde)]);
    } else {
      celdas = celdas.map((f) => [...f.slice(0, desde), ...f.slice(desde - n)]);
    }
    // Las celdas combinadas se corren, se agrandan o se achican como un rango.
    const combinadas = h.combinadas && h.combinadas.length
      ? { combinadas: ajustarCombinadas(h.combinadas, cambio) } : {};
    return {
      ...h,
      ...reglas,
      ...combinadas,
      celdas,
      formato: correrFormato(h.formato, eje, desde, n),
      anchos: eje === "col" ? correrIndices(h.anchos, desde, n) : h.anchos,
      altos: eje === "fila" ? correrIndices(h.altos, desde, n) : h.altos,
      ...(h.tablasHub ? { tablasHub: correrTablasHub(h.tablasHub, eje, desde, n) } : {}),
    };
  });
}

/* ── Ordenar ────────────────────────────────────────────────────────────────
 *
 * Ordena las FILAS ENTERAS por una columna. La fila 1 es el encabezado y se
 * queda donde está (el mismo criterio que los filtros). Las filas vacías van
 * al final, en cualquier sentido. `valores` es la hoja ya calculada: se ordena
 * por lo que se ve, no por el texto de la fórmula.
 *
 * Una fórmula que viaja con su fila sigue mirando a SU fila (=B5*C5 pasa a ser
 * =B2*C2 si la fila 5 termina segunda). */

function claveDeOrden(v) {
  if (v === "" || v == null) return [3, 0];
  if (typeof v === "number") return [0, v];
  if (typeof v === "boolean") return [2, v ? 1 : 0];
  const f = fechaASerial(String(v));
  if (f !== null) return [0, f];
  return [1, String(v)];
}

const comparador = new Intl.Collator("es", { numeric: true, sensitivity: "base" });

export function ordenar(hoja, col, ascendente, valores) {
  const filas = hoja.celdas.length;
  const conAlgo = (r) => hoja.celdas[r].some((x) => x !== "");
  const orden = [];
  for (let r = 1; r < filas; r += 1) orden.push(r);
  const claves = new Map(orden.map((r) => [r, claveDeOrden(valores[r] ? valores[r][col] : "")]));
  orden.sort((a, b) => {
    // Las filas totalmente vacías, siempre al fondo.
    const va = conAlgo(a) ? 0 : 1;
    const vb = conAlgo(b) ? 0 : 1;
    if (va !== vb) return va - vb;
    const [ta, xa] = claves.get(a);
    const [tb, xb] = claves.get(b);
    if (ta === 3 || tb === 3) return ta === tb ? a - b : ta - tb; // sin dato en esa columna: después
    if (ta !== tb) return ascendente ? ta - tb : tb - ta;
    const cmp = ta === 1 ? comparador.compare(xa, xb) : xa - xb;
    return cmp ? (ascendente ? cmp : -cmp) : a - b; // empate: como estaban
  });
  if (orden.every((r, i) => r === i + 1)) return hoja;

  const celdas = [hoja.celdas[0]];
  const nuevaDe = new Map([[0, 0]]); // fila vieja → fila nueva
  orden.forEach((vieja, i) => {
    const nueva = i + 1;
    nuevaDe.set(vieja, nueva);
    celdas.push(vieja === nueva
      ? hoja.celdas[vieja]
      : hoja.celdas[vieja].map((v) => desplazarFormula(v, nueva - vieja, 0)));
  });
  const formato = {};
  for (const [ref, f] of Object.entries(hoja.formato || {})) {
    const p = parseRef(ref);
    if (p && nuevaDe.has(p.fila)) formato[refDe(nuevaDe.get(p.fila), p.col)] = f;
  }
  const altos = {};
  for (const [k, v] of Object.entries(hoja.altos || {})) {
    if (nuevaDe.has(Number(k))) altos[nuevaDe.get(Number(k))] = v;
  }
  // Las combinadas de una fila viajan con ella; las de varias se separan.
  const combinadas = hoja.combinadas && hoja.combinadas.length
    ? { combinadas: ordenarCombinadas(hoja.combinadas, nuevaDe) } : {};
  return { ...hoja, celdas, formato, altos, ...combinadas };
}

/* ── Buscar ─────────────────────────────────────────────────────────────────
 * La próxima celda, después de `desde`, cuyo valor A LA VISTA contiene el
 * texto. Da la vuelta al llegar al final. null si no hay ninguna. */
export function buscar(valores, texto, desde) {
  const q = String(texto || "").trim().toLowerCase();
  const filas = valores.length;
  const cols = filas ? valores[0].length : 0;
  if (!q || !filas || !cols) return null;
  const inicio = desde ? desde.r * cols + desde.c : -1;
  for (let k = 1; k <= filas * cols; k += 1) {
    const i = (inicio + k + filas * cols) % (filas * cols);
    const r = Math.floor(i / cols);
    const c = i % cols;
    if (formatear(valores[r][c]).toLowerCase().includes(q)) return { r, c };
  }
  return null;
}

/* ── Bordes ─────────────────────────────────────────────────────────────────
 *
 * Cada celda guarda sus propios lados en `formato.bordes` (`{ t, r, b, l }`:
 * arriba, derecha, abajo, izquierda). Los tipos, como en Sheets, se aplican a
 * un RANGO y cada uno decide qué lados le tocan a cada celda según dónde está:
 * «exteriores» pinta sólo el perímetro, «interiores» sólo las líneas de
 * adentro. Devuelve el formato nuevo de la hoja. */

export const TIPOS_BORDE = [
  ["todos", "Todos los bordes"],
  ["exteriores", "Bordes exteriores"],
  ["interiores", "Bordes interiores"],
  ["horizontales", "Horizontales interiores"],
  ["verticales", "Verticales interiores"],
  ["superior", "Borde superior"],
  ["inferior", "Borde inferior"],
  ["izquierdo", "Borde izquierdo"],
  ["derecho", "Borde derecho"],
  ["ninguno", "Sin bordes"],
];

export function aplicarBordes(formato, rg, tipo) {
  const out = { ...(formato || {}) };
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const arriba = r === rg.r0;
      const abajo = r === rg.r1;
      const izq = c === rg.c0;
      const der = c === rg.c1;
      const lados = {
        todos: { t: 1, r: 1, b: 1, l: 1 },
        exteriores: { t: arriba, b: abajo, l: izq, r: der },
        interiores: { t: !arriba, b: !abajo, l: !izq, r: !der },
        horizontales: { t: !arriba, b: !abajo },
        verticales: { l: !izq, r: !der },
        superior: { t: arriba },
        inferior: { b: abajo },
        izquierdo: { l: izq },
        derecho: { r: der },
      }[tipo] || {};
      const ref = refDe(r, c);
      const nuevo = { ...(out[ref] || {}) };
      const bordes = tipo === "ninguno" ? {} : { ...(nuevo.bordes || {}) };
      for (const [k, v] of Object.entries(lados)) if (v) bordes[k] = 1;
      if (Object.keys(bordes).length) nuevo.bordes = bordes; else delete nuevo.bordes;
      if (Object.keys(nuevo).length) out[ref] = nuevo; else delete out[ref];
    }
  }
  return out;
}

/* ¿Un color de fondo es oscuro? Para poner el texto en blanco encima. */
export function esOscuro(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ""));
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16) / 255)
    .map((x) => (x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.4;
}

/* ── Σ: la suma rápida ──────────────────────────────────────────────────────
 * Con un rango elegido, la fórmula va debajo de cada columna. Con una celda
 * sola, se toman los números que tiene justo arriba (o, si no hay, a la
 * izquierda). Devuelve los cambios a escribir y la celda donde quedó. */
export function autoSuma(valores, rg, fn) {
  const esNum = (r, c) => !!valores[r] && typeof valores[r][c] === "number";
  if (rg.r0 !== rg.r1 || rg.c0 !== rg.c1) {
    const r = rg.r1 + 1;
    const cambios = [];
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      cambios.push({ r, c, v: `=${fn}(${refDe(rg.r0, c)}:${refDe(rg.r1, c)})` });
    }
    return { cambios, foco: { r, c: rg.c0 } };
  }
  const { r0: r, c0: c } = rg;
  let desde = r;
  while (desde > 0 && esNum(desde - 1, c)) desde -= 1;
  if (desde < r) return { cambios: [{ r, c, v: `=${fn}(${refDe(desde, c)}:${refDe(r - 1, c)})` }], foco: { r, c } };
  let izq = c;
  while (izq > 0 && esNum(r, izq - 1)) izq -= 1;
  if (izq < c) return { cambios: [{ r, c, v: `=${fn}(${refDe(r, izq)}:${refDe(r, c - 1)})` }], foco: { r, c } };
  return { cambios: [{ r, c, v: `=${fn}()` }], foco: { r, c } };
}

/* ── Limpiar y transformar datos ────────────────────────────────────────────
 *
 * Lo del menú Datos de Sheets que se usa todos los días al traer una planilla
 * de afuera. Todas trabajan sobre un rango, no tocan las FÓRMULAS (cambiarle
 * el texto a una fórmula es romperla sin avisar) y devuelven cuánto cambiaron,
 * para poder decirlo. */

const esFormula = (v) => typeof v === "string" && v.startsWith("=");
const todaLaHoja = (hoja) => ({ r0: 0, c0: 0, r1: hoja.celdas.length - 1, c1: anchoDe(hoja.celdas) - 1 });
const acotar = (hoja, rg) => {
  const t = todaLaHoja(hoja);
  return rg ? { r0: rg.r0, c0: rg.c0, r1: Math.min(rg.r1, t.r1), c1: Math.min(rg.c1, t.c1) } : t;
};

/* Buscar y reemplazar, sin distinguir mayúsculas. Sin rango, en toda la hoja. */
export function reemplazar(hoja, buscado, por, rango) {
  const q = String(buscado || "");
  if (!q) return { hoja, cuantas: 0 };
  const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  const rg = acotar(hoja, rango);
  const cambios = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const v = hoja.celdas[r][c];
      if (v === "" || esFormula(v)) continue;
      const n = v.replace(re, () => String(por ?? ""));
      if (n !== v) cambios.push({ r, c, v: n });
    }
  }
  return { hoja: escribir(hoja, cambios), cuantas: cambios.length };
}

/* Saca los espacios de las puntas y deja uno solo donde había varios: lo que
   hace que «Pérez » y «Pérez» no sean el mismo chofer en un BUSCARV. */
export function recortarEspacios(hoja, rango) {
  const rg = acotar(hoja, rango);
  const cambios = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const v = hoja.celdas[r][c];
      if (v === "" || esFormula(v)) continue;
      const n = v.trim().replace(/\s+/g, " ");
      if (n !== v) cambios.push({ r, c, v: n });
    }
  }
  return { hoja: escribir(hoja, cambios), cuantas: cambios.length };
}

/* Quita las filas repetidas ADENTRO del rango: dos filas son la misma si
   coinciden todas sus celdas del rango (sin mirar mayúsculas ni espacios de
   las puntas). Queda la primera; las que siguen suben a ocupar el lugar, y lo
   que está fuera del rango no se mueve. Las filas vacías no cuentan. */
export function quitarDuplicados(hoja, rango) {
  const rg = acotar(hoja, rango);
  const vistas = new Set();
  const quedan = []; // { de: fila original, celdas }
  let quitadas = 0;
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    const celdas = hoja.celdas[r].slice(rg.c0, rg.c1 + 1);
    const clave = celdas.map((v) => String(v).trim().toLowerCase()).join("\u0001");
    const vacia = celdas.every((v) => v === "");
    if (!vacia && vistas.has(clave)) { quitadas += 1; continue; }
    if (!vacia) vistas.add(clave);
    quedan.push({ de: r, celdas });
  }
  if (!quitadas) return { hoja, quitadas: 0 };
  const cambios = [];
  for (let i = 0; rg.r0 + i <= rg.r1; i += 1) {
    const r = rg.r0 + i;
    const fila = quedan[i];
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const v = fila ? desplazarFormula(fila.celdas[c - rg.c0], r - fila.de, 0) : "";
      if (v !== hoja.celdas[r][c]) cambios.push({ r, c, v });
    }
  }
  return { hoja: escribir(hoja, cambios), quitadas };
}

/* El separador que más aparece en una lista de textos (para «Dividir texto en
   columnas» en automático). A igualdad, gana el más específico. */
export function detectarSeparador(textos) {
  let mejor = null;
  let max = 0;
  for (const sep of [";", "\t", "|", ",", " "]) {
    const n = textos.reduce((t, x) => t + (String(x).split(sep).length - 1), 0);
    if (n > max) { max = n; mejor = sep; }
  }
  return mejor;
}

/* Parte cada celda de la PRIMERA columna del rango por el separador y reparte
   los pedazos hacia la derecha (pisa lo que haya ahí). Sin separador, lo
   detecta. */
export function dividirTexto(hoja, rango, separador) {
  const rg = acotar(hoja, rango);
  const c = rg.c0;
  const filas = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    const v = hoja.celdas[r][c];
    if (v !== "" && !esFormula(v)) filas.push({ r, v });
  }
  const sep = separador || detectarSeparador(filas.map((f) => f.v));
  if (!sep) return { hoja, cuantas: 0, separador: null };
  const cambios = [];
  let cuantas = 0;
  for (const { r, v } of filas) {
    const partes = v.split(sep).map((x) => x.trim());
    if (partes.length < 2) continue;
    cuantas += 1;
    partes.forEach((x, i) => cambios.push({ r, c: c + i, v: x }));
  }
  return { hoja: escribir(hoja, cambios), cuantas, separador: sep };
}

/* ── Copiar formato (la brocha) ─────────────────────────────────────────────
 * El patrón de formatos copiado se repite sobre todo el destino; una celda del
 * patrón sin formato deja la de destino sin formato. La NOTA no es formato: ni
 * se copia ni se pisa la que tenga la celda de destino. */
export function pegarFormato(formato, patron, rg) {
  const out = { ...(formato || {}) };
  const alto = patron.length;
  const ancho = patron[0] ? patron[0].length : 0;
  if (!alto || !ancho) return out;
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const ref = refDe(r, c);
      const { nota: _ajena, ...copiado } = patron[(r - rg.r0) % alto][(c - rg.c0) % ancho] || {};
      const nuevo = out[ref] && out[ref].nota ? { ...copiado, nota: out[ref].nota } : copiado;
      if (Object.keys(nuevo).length) out[ref] = nuevo; else delete out[ref];
    }
  }
  return out;
}

/* ── Colores alternos ───────────────────────────────────────────────────────
 * Franjas para leer una tabla larga sin perder el renglón. Es de la HOJA
 * (`hoja.alternos` guarda el nombre del juego) y no de cada celda: al ordenar
 * o insertar una fila las franjas siguen en su lugar. Un color puesto a mano
 * en una celda manda sobre la franja. */
export const ALTERNOS = {
  gris: { nombre: "Gris", cab: "#d9d9d9", franja: "#f3f3f3" },
  azul: { nombre: "Azul", cab: "#a4c2f4", franja: "#e8f0fe" },
  verde: { nombre: "Verde", cab: "#b6d7a8", franja: "#e9f5e3" },
  naranja: { nombre: "Naranja", cab: "#f9cb9c", franja: "#fdf0e2" },
  violeta: { nombre: "Violeta", cab: "#b4a7d6", franja: "#eeeaf7" },
};

/* El color de franja que le toca a una fila (null si ninguno): la fila 1 es
   el encabezado; de ahí para abajo, una sí y una no. */
export function colorAlterno(juego, r) {
  const j = ALTERNOS[juego];
  if (!j) return null;
  if (r === 0) return j.cab;
  return r % 2 === 0 ? j.franja : null;
}
